import type { Surface } from '../shared/types';
import {clipSurfaceCanvas} from './surfaceCanvasClip';

export type ShadowStyle = 'off' | 'soft' | 'offset';
export interface DrawingShadow { style: ShadowStyle; distance: number }
export const DEFAULT_DRAWING_SHADOW: DrawingShadow = { style: 'off', distance: 12 };
export const SHADOW_MAX_EDGE = 1080;
export function shadowForBrush(style: ShadowStyle, width: number): DrawingShadow {
  return { style, distance: Math.min(24, Math.max(1, Math.round((Number.isFinite(width) ? width : 36) / 3))) };
}

/** One bounded raster, rebuilt at stroke completion/options changes, never per dab. */
export function renderDrawingShadow(source: HTMLCanvasElement, target: HTMLCanvasElement, settings: DrawingShadow, surface?: Surface): void {
  if (settings.style === 'off') { target.width = 0; target.height = 0; return; }
  const scale = Math.min(1, SHADOW_MAX_EDGE / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale)), height = Math.max(1, Math.round(source.height * scale));
  if (target.width !== width || target.height !== height) { target.width = width; target.height = height; }
  const context = target.getContext('2d');
  if (!context) return;
  context.clearRect(0, 0, width, height);
  context.save();
  if(surface&&(surface.regions||surface.contours||surface.reservePanels||surface.role==='exclusion'))clipSurfaceCanvas(context,surface,width,height);
  const distance = Math.min(24, Math.max(1, Number.isFinite(settings.distance) ? settings.distance : DEFAULT_DRAWING_SHADOW.distance)) * scale;
  const blur = settings.style === 'soft' ? 6 * scale : 0;
  // Move the source outside the raster, bring only its shadow back into it.
  // Unlike erasing the source afterward, this retains translucent overlap correctly.
  const displacement = width + distance + blur * 4 + 1;
  context.shadowColor = settings.style === 'soft' ? 'rgba(19,34,29,0.55)' : 'rgba(19,34,29,0.82)';
  context.shadowBlur = blur;
  context.shadowOffsetX = displacement + distance;
  context.shadowOffsetY = distance;
  context.drawImage(source, -displacement, 0, width, height);
  context.restore();
}

/** Same ordering for the visible draft, cost sample, live preview and final image. */
export function drawDrawingComposite(context: CanvasRenderingContext2D, source: HTMLCanvasElement, shadow: HTMLCanvasElement | null | undefined, width: number, height: number): void {
  if (shadow?.width && shadow.height) context.drawImage(shadow, 0, 0, width, height);
  context.drawImage(source, 0, 0, width, height);
}

export function drawingCompositeBlob(source: HTMLCanvasElement, shadow?: HTMLCanvasElement | null): Promise<Blob | null> {
  if (!shadow?.width || !shadow.height) return new Promise(resolve => source.toBlob(resolve, 'image/png'));
  // Export alone needs a full-size composite. Release it after encoding; source/history stay flat.
  const result = source.ownerDocument.createElement('canvas');
  result.width = source.width; result.height = source.height;
  const context = result.getContext('2d');
  if (!context) return Promise.resolve(null);
  drawDrawingComposite(context, source, shadow, result.width, result.height);
  return new Promise(resolve => result.toBlob(blob => { result.width = 0; result.height = 0; resolve(blob); }, 'image/png'));
}
